import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  Paper,
  Box,
  Typography,
  Chip,
  Tooltip,
  Divider,
} from "@mui/material";
import Pagination from "@mui/lab/Pagination";
import * as actions from "../Releases/actions";
import { Link2, Mail, User, Monitor, Tv, Calendar, Copy, ClipboardCheck, ExternalLink, ShieldCheck } from 'lucide-react';
import _ from "lodash";
import { toastError, toastSuccess, copyToClipboard } from "../../utils";
import CustomShowModal from "../Common/customShowModal";
import SearchInput from '../../components/SearchInput';
import StatusBadge from '../../components/StatusBadge';
import ActionButton from '../../components/ActionButton';
import EmptyState from '../../components/EmptyState';

function CopyMeta({ icon, label, value, copyValue }) {
  const [copied, setCopied] = useState(false);
  if (!value || value === '-') {
    return (
      <Tooltip title={label} arrow placement="top">
        <span className="approved-release-card__meta-item">
          {icon} -
        </span>
      </Tooltip>
    );
  }
  return (
    <Tooltip title={copied ? "Copied!" : `${label} — click to copy`} arrow placement="top">
      <span
        className="approved-release-card__meta-item approved-release-card__meta-item--copyable"
        onClick={() => { copyToClipboard(copyValue || value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      >
        {icon} {value}
        {copied ? <ClipboardCheck size={11} className="copy-tick" /> : <Copy size={11} className="copy-icon" />}
      </span>
    </Tooltip>
  );
}

export default function Whitelisting(props) {
  const [PLinkNew, setPLinkNew] = useState([]);
  const [PLinkOld, setPLinkOld] = useState([]);
  const [confirmModal, setConfirmModal] = useState({ open: false, id: null, action: null });
  const [count, setCount] = useState(0);

  const [filters, setFilters] = useState({
    limit: 50,
    offset: 0,
    page: 1,
    search: "",
  });

  const delaySearch = useCallback(
    _.debounce((filters) => onGetPlinkOld(filters), 500),
    []
  );

  useEffect(() => {
    document.title = 'WL | Whitelisting';
    return () => { document.title = 'Fronic Media Admin'; };
  }, []);

  useEffect(() => {
    onGetPlinkOld(filters);
    onGetPLinkNew();
  }, []);

  const onGetPlinkOld = (filters) => {
    props.setLoader(true);
    actions
      .getWhitelistingOldAdmin({ ...filters })
      .then((res) => {
        props.setLoader(false);
        setPLinkOld(res.body);
        setCount(res.total);
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || 'Operation failed');
      });
  };

  const onPageChange = (event, page) => {
    const offset = (page - 1) * filters.limit;
    const newFilters = { ...filters, page, offset };
    setFilters(newFilters);
    onGetPlinkOld(newFilters);
  };

  const onChangeSearch = (e) => {
    const newFilters = { ...filters, search: e.target.value };
    setFilters(newFilters);
    delaySearch(newFilters);
  };

  const onGetPLinkNew = () => {
    props.setLoader(true);
    actions
      .getWhitelistingRequestAdmin()
      .then((res) => {
        setPLinkNew(res.body);
        props.setLoader(false);
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || 'Failed to load whitelisting requests');
      });
  };

  let totalPages = Math.ceil(Number(count) / filters.limit);

  const openConfirmModal = (id, action) => {
    setConfirmModal({ open: true, id, action });
  };

  const closeConfirmModal = () => {
    setConfirmModal({ open: false, id: null, action: null });
  };

  const onConfirmAction = () => {
    const { id, action } = confirmModal;
    closeConfirmModal();
    props.setLoader(true);
    actions
      .updateWhitelistingStatusAdmin({ status: action, whitelist_id: id })
      .then((res) => {
        onGetPLinkNew();
        onGetPlinkOld(filters);
        toastSuccess(res.message || 'Whitelisting status updated successfully');
        props.setLoader(false);
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || 'Failed to update whitelisting status');
      });
  };

  const renderCard = (item, isNew) => (
    <Paper
      key={item.whitelist_id}
      className="approved-release-card"
      style={{ cursor: 'default' }}
    >
      <div className="approved-release-card__artwork">
        <div className="approved-release-card__artwork-fallback">
          <ShieldCheck size={20} color="#64748B" />
          <span>WL</span>
        </div>
      </div>

      <div className="approved-release-card__body">
        <div className="approved-release-card__top">
          <div className="approved-release-card__title-row">
            <Typography className="approved-release-card__title">
              {item.channel_name || 'Unknown Channel'}
            </Typography>
            <Chip
              label={item.platform || '-'}
              size="small"
              sx={{
                backgroundColor: 'rgba(108, 99, 255, 0.12)',
                color: '#A78BFA',
                border: '1px solid rgba(108, 99, 255, 0.2)',
                fontWeight: 500,
                fontSize: '0.72rem',
                height: 22,
                ml: 1,
              }}
            />
            {item.channel_type && (
              <Chip
                label={item.channel_type}
                size="small"
                sx={{
                  backgroundColor: 'rgba(16, 185, 129, 0.1)',
                  color: '#10B981',
                  border: '1px solid rgba(16, 185, 129, 0.2)',
                  fontWeight: 500,
                  fontSize: '0.72rem',
                  height: 22,
                  ml: 0.5,
                }}
              />
            )}
          </div>
          {item.URL && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0, marginLeft: 'auto' }}>
              <Link2 size={14} style={{ display: 'block', color: '#6C63FF' }} />
              <span style={{ color: '#94A3B8', fontSize: '0.9rem', fontWeight: 500 }}>URL:</span>
              <CopyMeta icon={null} label="URL" value="Copy" copyValue={item.URL} />
              <Tooltip title="Open in new tab" arrow placement="top">
                <a
                  href={item.URL}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: '#6C63FF', textDecoration: 'none', fontSize: '0.85rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                  onClick={e => e.stopPropagation()}
                >
                  <ExternalLink size={13} /> Open
                </a>
              </Tooltip>
            </span>
          )}
        </div>

        <div className="approved-release-card__meta">
          <span className="approved-release-card__id">FM-WL-{item.whitelist_id}</span>
          <CopyMeta icon={<User size={13} />} label="Name" value={item.name} />
          <CopyMeta icon={<Mail size={13} />} label="Email" value={item.email} />
          {item.date && (
            <span className="approved-release-card__meta-item" style={{ color: '#F59E0B', fontWeight: 600, fontFamily: 'monospace', letterSpacing: '0.5px' }}>
              <Calendar size={13} style={{ color: '#F59E0B' }} /> {item.date}
            </span>
          )}
          <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
            {isNew ? (
              <>
                <ActionButton variant="success" sx={{ py: 0.2, px: 1.5, fontSize: '0.75rem' }} onClick={() => openConfirmModal(item.whitelist_id, 'approved')}>
                  Accept
                </ActionButton>
                <ActionButton variant="danger" sx={{ py: 0.2, px: 1.5, fontSize: '0.75rem' }} onClick={() => openConfirmModal(item.whitelist_id, 'rejected')}>
                  Reject
                </ActionButton>
              </>
            ) : (
              <StatusBadge status={item.status} />
            )}
          </div>
        </div>
      </div>
    </Paper>
  );

  return (
    <Grid container>
      {/* ========== Review Required Section ========== */}
      <Grid item xs={12}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2}>
          <Typography sx={{ color: '#F1F5F9', fontWeight: 600, fontSize: '1rem' }}>
            Review Required
          </Typography>
          <Chip
            icon={<ShieldCheck size={14} />}
            label={`${PLinkNew.length} Pending`}
            sx={{
              backgroundColor: 'rgba(245, 158, 11, 0.12)',
              color: '#F59E0B',
              border: '1px solid rgba(245, 158, 11, 0.25)',
              fontWeight: 600,
              fontSize: '0.85rem',
              height: 34,
              flexShrink: 0,
              '& .MuiChip-icon': { color: '#F59E0B' },
            }}
          />
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Box className="approved-release-list" sx={{ mb: 3 }}>
          {PLinkNew.length ? (
            PLinkNew.map((item) => renderCard(item, true))
          ) : (
            <Paper sx={{ p: 0 }}>
              <table style={{ width: '100%' }}><tbody>
                <EmptyState message="No Pending Requests" colSpan={1} />
              </tbody></table>
            </Paper>
          )}
        </Box>
      </Grid>

      {/* ========== Divider ========== */}
      <Grid item xs={12}>
        <Divider sx={{ borderColor: 'var(--border)', my: 1 }} />
      </Grid>

      {/* ========== History Section ========== */}
      <Grid item xs={12}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2} mt={2}>
          <Box sx={{ maxWidth: 420, flex: 1 }}>
            <SearchInput
              onChange={onChangeSearch}
              value={filters.search}
              placeholder="Search whitelisting history..."
            />
          </Box>
          <Box display="flex" alignItems="center" gap={1.5} flexShrink={0}>
            <Typography sx={{ color: '#F1F5F9', fontWeight: 600, fontSize: '1rem' }}>
              History
            </Typography>
            <Chip
              icon={<ShieldCheck size={14} />}
              label={`${count} Total`}
              sx={{
                backgroundColor: 'rgba(108, 99, 255, 0.12)',
                color: '#6C63FF',
                border: '1px solid rgba(108, 99, 255, 0.25)',
                fontWeight: 600,
                fontSize: '0.85rem',
                height: 34,
                flexShrink: 0,
                '& .MuiChip-icon': { color: '#6C63FF' },
              }}
            />
          </Box>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Box className="approved-release-list">
          {PLinkOld.length ? (
            PLinkOld.map((item) => renderCard(item, false))
          ) : (
            <Paper sx={{ p: 0 }}>
              <table style={{ width: '100%' }}><tbody>
                <EmptyState message="No Whitelisting History Found" colSpan={1} />
              </tbody></table>
            </Paper>
          )}
        </Box>
        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination
              count={totalPages}
              showFirstButton
              showLastButton
              onChange={onPageChange}
              page={filters.page || 1}
            />
          </Box>
        )}
      </Grid>

      {/* ========== Confirmation Modal ========== */}
      <CustomShowModal
        handleClose={closeConfirmModal}
        title={confirmModal.action === 'approved' ? 'Accept Whitelisting' : 'Reject Whitelisting'}
        open={confirmModal.open}
        maxWidth="sm"
      >
        <Typography sx={{ color: '#94A3B8', fontSize: '0.9rem', mb: 2 }}>
          {confirmModal.action === 'approved'
            ? 'Are you sure you want to approve this whitelisting request? The user will be notified.'
            : 'Are you sure you want to reject this whitelisting request? This action cannot be undone.'
          }
        </Typography>
        <Box display="flex" justifyContent="flex-end" gap={1} py={1}>
          <ActionButton variant="info" onClick={closeConfirmModal}>
            Cancel
          </ActionButton>
          <ActionButton
            variant={confirmModal.action === 'approved' ? 'success' : 'danger'}
            onClick={onConfirmAction}
          >
            {confirmModal.action === 'approved' ? 'Accept' : 'Reject'}
          </ActionButton>
        </Box>
      </CustomShowModal>
    </Grid>
  );
}
